/**
 * CargarProductosExcel.jsx - VERSIÓN COMPLETA
 * Incluye: Excel, Carga Manual, Prioridad de Precios, Duplicados y Borrado Masivo.
 */

import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { FIRESTORE_DB } from "../../../Database/Database";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { NavBarDashboard } from "../../../Components/NavBarDashboard/NavBarDashboard";
import { FooterDashboard } from "../../../Components/FooterDashboard/FooterDashboard";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  ProgressBar,
  ListGroup,
  Badge,
  Spinner,
  Alert
} from "react-bootstrap";

const CargarProductosExcel = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [existingProducts, setExistingProducts] = useState([]);
  const [selectedToDelete, setSelectedToDelete] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [productoIndividual, setProductoIndividual] = useState({
    codigoProducto: "", nombreProducto: "", categoria: "", proveedor: "", 
    stock: "", precioCosto: "", multiplicador: "", atributo: "", 
    precioVenta: "", precioFinal: ""
  });

  // --- UTILIDADES ---
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number(value.toFixed(2));
    let str = String(value).trim().replace(/ /g, "");
    if (str.includes(",") && str.includes(".")) str = str.replace(/\./g, "").replace(",", ".");
    else if (str.includes(",")) str = str.replace(",", ".");
    const n = Number(str);
    return isNaN(n) ? 0 : Number(n.toFixed(2));
  };

  const roundToAttractivePrice = (num) => {
    if (num <= 1000) return Math.ceil(num / 100) * 100; 
    return (Math.ceil(num / 1000) * 1000) - 100;
  };

  const calcularPreciosCompuestos = (costo, mult, pVentaManual, pFinalManual) => {
    if (pFinalManual && parseNumber(pFinalManual) > 0) {
      return {
        precioVenta: pVentaManual ? parseNumber(pVentaManual) : parseNumber(pFinalManual),
        precioFinal: parseNumber(pFinalManual)
      };
    }
    const c = parseNumber(costo);
    const m = parseNumber(mult) || 1;
    const pv = Number((c * m).toFixed(2));
    return { precioVenta: pv, precioFinal: roundToAttractivePrice(pv) };
  };

  const existeProducto = (sku) => existingProducts.some(p => p.codigoProducto?.toUpperCase() === sku?.toUpperCase());

  // --- ACCIONES DE FIREBASE ---
  const loadExistingProducts = async () => {
    const snap = await getDocs(collection(FIRESTORE_DB, "productos"));
    setExistingProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { loadExistingProducts(); }, []);

  const deleteSelected = async () => {
    if (selectedToDelete.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedToDelete.length} productos seleccionados?`)) return;
    setLoading(true);
    for (const id of selectedToDelete) { await deleteDoc(doc(FIRESTORE_DB, "productos", id)); }
    await loadExistingProducts();
    setSelectedToDelete([]);
    setLoading(false);
  };

  // BOTÓN RESTAURADO: BORRAR TODA LA BASE
  const deleteAllProducts = async () => {
    if (!window.confirm("¡ATENCIÓN! ¿Estás seguro de que querés borrar TODO el inventario? Esta acción no se puede deshacer.")) return;
    setLoading(true);
    try {
      for (const p of existingProducts) {
        await deleteDoc(doc(FIRESTORE_DB, "productos", p.id));
      }
      alert("Base de datos limpiada con éxito.");
      await loadExistingProducts();
    } catch (e) { alert("Error al borrar la base"); }
    setLoading(false);
  };

  // --- CARGA EXCEL ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      setRows(XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: null }));
    };
    reader.readAsBinaryString(file);
  };

  const uploadToFirebase = async () => {
    setLoading(true); setProgress(0);
    const productsRef = collection(FIRESTORE_DB, "productos");
    let currentParentCode = null;
    let currentParentData = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.codigoProducto) {
        currentParentCode = String(row.codigoProducto).toUpperCase().trim();
        currentParentData = { nombreProducto: row.nombreProducto, categoria: row.categoria, proveedor: row.proveedor };
      }
      if (!currentParentCode) continue;

      const rawAttr = row.atributo ? String(row.atributo).trim() : "";
      const sku = rawAttr === "" ? currentParentCode : `${currentParentCode}${rawAttr.substring(0, 5).toUpperCase()}`;

      if (!existeProducto(sku)) {
        const { precioVenta, precioFinal } = calcularPreciosCompuestos(row.precioCosto, row.multiplicador, row.precioVenta, row.precioFinal);
        await addDoc(productsRef, {
          codigoProducto: sku,
          codigoPadre: currentParentCode,
          atributo: rawAttr || "Único",
          ...currentParentData,
          stock: parseNumber(row.stock),
          precioCosto: parseNumber(row.precioCosto),
          multiplicador: parseNumber(row.multiplicador) || 1,
          precioVenta, precioFinal,
          cantidadVendida: 0, fechaIngreso: serverTimestamp()
        });
      }
      setProgress(Math.round(((i + 1) / rows.length) * 100));
    }
    alert("Proceso finalizado.");
    await loadExistingProducts();
    setRows([]); setLoading(false);
  };

  // --- CARGA MANUAL ---
  const handleChangeIndividual = (e) => {
    const { name, value } = e.target;
    setProductoIndividual(prev => {
      const next = { ...prev, [name]: value };
      if (["precioCosto", "multiplicador"].includes(name) && !next.precioFinal) {
        const calcs = calcularPreciosCompuestos(next.precioCosto, next.multiplicador, next.precioVenta, next.precioFinal);
        next.precioVenta = calcs.precioVenta;
        next.precioFinal = calcs.precioFinal;
      }
      return next;
    });
  };

  const guardarIndividual = async () => {
    const skuFinal = (productoIndividual.atributo ? `${productoIndividual.codigoProducto}${productoIndividual.atributo.substring(0, 5).toUpperCase()}` : productoIndividual.codigoProducto).toUpperCase();
    if (existeProducto(skuFinal)) return alert("El SKU ya existe.");
    
    setLoading(true);
    await addDoc(collection(FIRESTORE_DB, "productos"), {
      ...productoIndividual,
      codigoProducto: skuFinal,
      codigoPadre: productoIndividual.codigoProducto.toUpperCase(),
      atributo: productoIndividual.atributo || "Único",
      precioCosto: parseNumber(productoIndividual.precioCosto),
      precioVenta: parseNumber(productoIndividual.precioVenta),
      precioFinal: parseNumber(productoIndividual.precioFinal),
      stock: parseNumber(productoIndividual.stock),
      fechaIngreso: serverTimestamp()
    });
    setShowModal(false);
    await loadExistingProducts();
    setLoading(false);
  };

  return (
    <>
      <NavBarDashboard />
      <Container className="py-4">
        <Row className="g-4">
          <Col md={5}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <Card.Title>Importación</Card.Title>
                <Form.Control type="file" className="my-3" onChange={handleFileUpload} />
                <div className="d-grid gap-2">
                  <Button variant="primary" onClick={() => setShowModal(true)}>Carga Individual</Button>
                  <Button variant="success" onClick={uploadToFirebase} disabled={rows.length === 0 || loading}>Subir Excel</Button>
                </div>
                {loading && <ProgressBar now={progress} className="mt-3" />}
              </Card.Body>
            </Card>
          </Col>

          <Col md={7}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title className="mb-0">Inventario ({existingProducts.length})</Card.Title>
                  <div className="d-flex gap-2">
                    {/* BOTONES DE ELIMINACIÓN RESTAURADOS */}
                    <Button variant="danger" size="sm" onClick={deleteAllProducts} disabled={loading || existingProducts.length === 0}>Borrar Todo</Button>
                    <Button variant="outline-danger" size="sm" onClick={deleteSelected} disabled={selectedToDelete.length === 0}>Borrar Selección</Button>
                  </div>
                </div>
                <ListGroup variant="flush" style={{maxHeight: '400px', overflowY: 'auto'}}>
                  {existingProducts.map(p => (
                    <ListGroup.Item key={p.id} className="d-flex justify-content-between align-items-center small">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check checked={selectedToDelete.includes(p.id)} onChange={() => setSelectedToDelete(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])} />
                        <div><b>{p.codigoProducto}</b> - {p.nombreProducto}</div>
                      </div>
                      <div className="text-end fw-bold text-primary">${p.precioFinal}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">Carga Manual</Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-2">
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Código Base</Form.Label><Form.Control name="codigoProducto" onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={8}><Form.Group className="mb-2"><Form.Label>Nombre</Form.Label><Form.Control name="nombreProducto" onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Variante</Form.Label><Form.Control name="atributo" onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Costo</Form.Label><Form.Control name="precioCosto" onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Multiplicador</Form.Label><Form.Control name="multiplicador" onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Precio Venta</Form.Label><Form.Control name="precioVenta" value={productoIndividual.precioVenta} onChange={handleChangeIndividual} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label className="text-primary fw-bold">Precio Final</Form.Label><Form.Control name="precioFinal" value={productoIndividual.precioFinal} className="bg-warning bg-opacity-10" onChange={handleChangeIndividual} /></Form.Group></Col>
            </Row>
            <Alert variant="info" className="mt-3 py-2 small">El Precio Final manual tiene prioridad sobre el multiplicador.</Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={guardarIndividual} disabled={loading}>Guardar</Button>
        </Modal.Footer>
      </Modal>
      <FooterDashboard />
    </>
  );
};

export { CargarProductosExcel };